import { type ReactElement } from 'react';
import { r, s } from '@/theme/tokens';
import { Box, Skeleton, TableCell, TableRow } from '@/ui/primitives';
import { KeyboardArrowRightOutlinedIcon } from '@/ui/primitives/icons';
import { tableV2ChevronCellSx } from './TableV2ChevronCellStyles';
import {
  tableV2ChevronColumnWidth,
  tableV2ChevronIconSize,
  tableV2SkeletonChevronOffsetTransform,
} from './TableV2Constants';
import { resolveTableV2SkeletonRowHeight } from './TableV2Heights';
import { tableV2RowSurfaceSx } from './TableV2RowSurface';

export type TableV2SkeletonColumnKind =
  | 'actions'
  | 'chevron'
  | 'dateTime'
  | 'duration'
  | 'empty'
  | 'metric'
  | 'name'
  | 'nameSingle'
  | 'passRate'
  | 'project'
  | 'status'
  | 'tags';

const tableV2SkeletonWidths = ['68%', '58%', '72%', '62%'] as const;

const getTableV2SkeletonWidth = (rowIndex: number, offset = 0): string =>
  tableV2SkeletonWidths[(rowIndex + offset) % tableV2SkeletonWidths.length];

const tableV2SkeletonPrimaryLineHeight = '14px';
const tableV2SkeletonSecondaryLineHeight = '14px';
const tableV2SkeletonMetricLineHeight = '14px';

const skeletonSx = {
  borderRadius: r('pill'),
  transform: 'none',
} as const;

const tableV2MetricSkeletonCellSx = {
  pl: s('none'),
  pr: s('sm'),
  textAlign: 'left',
} as const;

const TableV2SkeletonChevron = (): ReactElement => (
  <Box
    component="span"
    sx={{
      alignItems: 'center',
      borderRadius: r('pill'),
      boxSizing: 'border-box',
      color: (theme) => theme.app.colors.textMuted,
      display: 'inline-flex',
      flex: '0 0 auto',
      height: tableV2ChevronColumnWidth,
      justifyContent: 'center',
      left: '50%',
      lineHeight: 0,
      p: s('none'),
      position: 'absolute',
      textAlign: 'center',
      top: '50%',
      transform: tableV2SkeletonChevronOffsetTransform,
      verticalAlign: 'middle',
      width: tableV2ChevronColumnWidth,
      '& .MuiSvgIcon-root': {
        fontSize: tableV2ChevronIconSize,
        height: tableV2ChevronIconSize,
        width: tableV2ChevronIconSize,
      },
    }}
  >
    <KeyboardArrowRightOutlinedIcon
      sx={{
        display: 'block',
        opacity: 0.55,
      }}
    />
  </Box>
);

const TableV2SkeletonActions = (): ReactElement => (
  <Skeleton height="18px" sx={{ transform: 'none' }} variant="circular" width="18px" />
);

const TableV2SkeletonStatus = (): ReactElement => (
  <Skeleton height="20px" sx={skeletonSx} variant="rounded" width="58px" />
);

const TableV2SkeletonTags = ({ rowIndex }: { rowIndex: number }): ReactElement => (
  <Skeleton
    height="18px"
    sx={skeletonSx}
    variant="rounded"
    width={rowIndex % 2 === 0 ? '54px' : '46px'}
  />
);

export const TableV2SkeletonCell = ({
  kind,
  rowIndex,
}: {
  kind: TableV2SkeletonColumnKind;
  rowIndex: number;
}): ReactElement => {
  switch (kind) {
    case 'actions':
      return <TableV2SkeletonActions />;
    case 'chevron':
      return <TableV2SkeletonChevron />;
    case 'dateTime':
      return (
        <Skeleton
          height={tableV2SkeletonSecondaryLineHeight}
          sx={skeletonSx}
          variant="rounded"
          width={getTableV2SkeletonWidth(rowIndex)}
        />
      );
    case 'duration':
      return (
        <Skeleton
          height={tableV2SkeletonMetricLineHeight}
          sx={skeletonSx}
          variant="rounded"
          width={getTableV2SkeletonWidth(rowIndex, 3)}
        />
      );
    case 'empty':
      return <Box aria-hidden="true" />;
    case 'metric':
      return (
        <Skeleton
          height={tableV2SkeletonMetricLineHeight}
          sx={skeletonSx}
          variant="rounded"
          width={getTableV2SkeletonWidth(rowIndex, 3)}
        />
      );
    case 'name':
      return (
        <Box sx={{ display: 'grid', gap: s('md'), minWidth: 0 }}>
          <Skeleton
            height={tableV2SkeletonPrimaryLineHeight}
            sx={skeletonSx}
            variant="rounded"
            width={getTableV2SkeletonWidth(rowIndex)}
          />
          <Skeleton
            height={tableV2SkeletonSecondaryLineHeight}
            sx={skeletonSx}
            variant="rounded"
            width={getTableV2SkeletonWidth(rowIndex, 1)}
          />
        </Box>
      );
    case 'nameSingle':
      return (
        <Skeleton
          height={tableV2SkeletonPrimaryLineHeight}
          sx={skeletonSx}
          variant="rounded"
          width={getTableV2SkeletonWidth(rowIndex)}
        />
      );
    case 'passRate':
      return (
        <Skeleton
          height={tableV2SkeletonMetricLineHeight}
          sx={skeletonSx}
          variant="rounded"
          width={getTableV2SkeletonWidth(rowIndex, 2)}
        />
      );
    case 'project':
      return (
        <Box sx={{ alignItems: 'center', display: 'flex', minWidth: 0 }}>
          <Skeleton
            height={tableV2SkeletonPrimaryLineHeight}
            sx={skeletonSx}
            variant="rounded"
            width={getTableV2SkeletonWidth(rowIndex, 1)}
          />
        </Box>
      );
    case 'status':
      return <TableV2SkeletonStatus />;
    case 'tags':
      return <TableV2SkeletonTags rowIndex={rowIndex} />;
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
};

export const TableV2SkeletonRow = ({
  columns,
  isLastVisibleRow,
  rowIndex,
  showLastDivider = false,
}: {
  columns: TableV2SkeletonColumnKind[];
  isLastVisibleRow: boolean;
  rowIndex: number;
  showLastDivider?: boolean;
}): ReactElement => {
  const rowHeight = resolveTableV2SkeletonRowHeight({ isLastVisibleRow, showLastDivider });

  return (
    <TableRow
      sx={[
        tableV2RowSurfaceSx,
        {
          height: rowHeight,
          maxHeight: rowHeight,
          '&&': {
            height: rowHeight,
            maxHeight: rowHeight,
          },
          '&& > td': {
            boxSizing: 'border-box',
            borderBottom:
              isLastVisibleRow && !showLastDivider
                ? 0
                : (theme) => `1px solid ${theme.app.colors.border}`,
            height: rowHeight,
            maxHeight: rowHeight,
            py: s('none'),
            verticalAlign: 'middle',
          },
        },
      ]}
    >
      {columns.map((kind, columnIndex) => (
        <TableCell
          key={`${kind}-${String(columnIndex)}`}
          align={kind === 'actions' || kind === 'chevron' ? 'center' : undefined}
          sx={
            kind === 'chevron'
              ? tableV2ChevronCellSx
              : kind === 'metric'
                ? tableV2MetricSkeletonCellSx
                : undefined
          }
        >
          <TableV2SkeletonCell kind={kind} rowIndex={rowIndex} />
        </TableCell>
      ))}
    </TableRow>
  );
};
